/**
 * HR-portal FTUE — Severance Elections page (`/benefits`).
 *
 * Owns the three on-page follow-ups to the `nav-severance-elections` sidebar
 * nudge (registered by the layout hook, `use-portal-hr-ftue`): the "Cohort
 * settings" button, the "Download CSV" button, and the results table. Each is
 * its own step (own phase, single-concept chrome) rather than a shared-phase
 * group, matching the sibling severance-import steps -- not the People
 * ready-branch or the HRIS wizard pair.
 *
 * Mounted from `BenefitsElections`. The layout hook owns the flow start and
 * the nav step that leads here; this hook only adds the follow-up steps
 * (order x7-x9) so their `condition`s can close over the page's live state,
 * matching the job-seeker pattern (`use-opportunities-ftue`, `use-learn-ftue`).
 *
 * `<SeveranceEntitlementGate>` does not mount `BenefitsElections` until the
 * org's severance entitlement is known and granted, so a mounted page already
 * implies entitlement -- these steps' `condition`s do NOT re-check
 * `features.severance` (same reasoning as `use-portal-severance-ftue`, the
 * Import Terms page's sibling hook).
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

const BENEFITS_PATH = '/benefits'

export interface PortalSeveranceElectionsFtueArgs {
  /** Whether the signed-in stakeholder can change cohort settings. */
  canManageCohort?: boolean
  /**
   * The page's own data has finished its initial fetch and did not error
   * (`!isLoading && !isError` from `usePortalBenefits`). The Cohort
   * settings / Download CSV buttons and the table are only meaningful once
   * this settles. Defaults to `true` when the hook is called with no args.
   */
  ready?: boolean
  /**
   * `rows.length > 0` -- the results table is only rendered once there is
   * something to show; before that the page renders an empty-state card
   * instead. Defaults to `false` when the hook is called with no args, so the
   * table step never activates before the caller says otherwise.
   */
  hasRows?: boolean
}

/**
 * Registers the three Severance Elections on-page steps and returns the
 * anchor refs for the "Cohort settings" button, the "Download CSV" button,
 * and the results `<Card>`. Call once from `BenefitsElections`.
 */
export function usePortalSeveranceElectionsFtue({
  ready = true,
  hasRows = false,
  canManageCohort = true,
}: PortalSeveranceElectionsFtueArgs = {}) {
  const cohortSettingsAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceCohortSettingsCta)
  const downloadCsvAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceDownloadCsvCta)
  const tableAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceElectionsTable)

  // The steps are registered once below, so their `condition` closures must
  // read the current values rather than the mount-time snapshot.
  const readyRef = useRef(ready)
  readyRef.current = ready
  const hasRowsRef = useRef(hasRows)
  hasRowsRef.current = hasRows
  const canManageCohortRef = useRef(canManageCohort)
  canManageCohortRef.current = canManageCohort

  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.severanceCohortSettings,
        phase: PORTAL_HR_STEP.severanceCohortSettings,
        order: PORTAL_HR_ORDER.severanceCohortSettings,
        trigger: { type: 'immediate' },
        condition: () =>
          onPortalPath(BENEFITS_PATH) && readyRef.current && canManageCohortRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceCohortSettingsCta,
        placement: 'bottom',
        // The anchor is the single "Cohort settings" button; clicking it opens
        // the settings dialog (intent), so completing the step there is right.
        dismissOnAction: true,
        content: {
          body: 'Set the election deadline and default terms for this cohort here.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.severanceDownloadCsv,
        phase: PORTAL_HR_STEP.severanceDownloadCsv,
        order: PORTAL_HR_ORDER.severanceDownloadCsv,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(BENEFITS_PATH) && readyRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceDownloadCsvCta,
        placement: 'bottom',
        // The anchor is the single "Download CSV" button; clicking it starts
        // the export (intent), so completing the step there is right.
        dismissOnAction: true,
        content: {
          body: 'Export the full elections list anytime.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.severanceTable,
        phase: PORTAL_HR_STEP.severanceTable,
        order: PORTAL_HR_ORDER.severanceTable,
        trigger: { type: 'immediate' },
        // Only a candidate once there is a populated results table to point
        // at -- before that the page renders an empty-state card instead, so
        // this step is skipped (not marked seen) rather than blocking the
        // rest of the flow (see the note in `flow.ts`).
        condition: () => onPortalPath(BENEFITS_PATH) && readyRef.current && hasRowsRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceElectionsTable,
        placement: 'top',
        // A whole table, not one control: no `dismissOnAction`, just the
        // explicit "Got it" ack.
        content: {
          body: "Edit is disabled once an election is locked - that just means it's final.",
          dismissLabel: 'Got it',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — start the shared flow so
    // the engine re-picks the next eligible step. Idempotent; the layout hook
    // owns the actual start rule.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when readiness or row count changes (a plain re-render does not
  // re-run the engine). If the active step is one of this hook's own three and
  // its `condition` has since flipped false while still unseen, mark it seen so
  // the tour advances cleanly; otherwise just re-pick the candidate. Scoped to
  // this hook's own step ids so it never retires a layout-owned or sibling-hook
  // step. Mirrors `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ready/hasRows intentionally re-trigger evaluation; live state is read via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    const ownStepIds: string[] = [
      PORTAL_HR_STEP.severanceCohortSettings,
      PORTAL_HR_STEP.severanceDownloadCsv,
      PORTAL_HR_STEP.severanceTable,
    ]
    if (activeStepId && ownStepIds.includes(activeStepId) && !seen.has(activeStepId)) {
      const step = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (step?.condition && !step.condition()) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [ready, hasRows, canManageCohort])

  return {
    /** Attach to the "Cohort settings" `<Button>`. */
    cohortSettingsAnchor,
    /** Attach to the "Download CSV" `<Button>`. */
    downloadCsvAnchor,
    /** Attach to the results `<Card>` (not the empty-state card). */
    tableAnchor,
  }
}
